<template>
    <footer class="footer">
        <div class="container">
            <div class="footer__wrapper">
                <div class="footer__logo">
                    <z-logo
                        :is-index="isIndex"
                        site-dir="/"
                        :src="logo.linkFile" />
                </div>
                <div class="footer__contacts footer-contact">
                    <div
                        v-for="(contact, idx) in contacts"
                        :key="idx"
                        class="footer-contact__item hide-me--v-tablet">
                        <p
                            class="footer-contact__title"
                            v-html="contact.title"></p>
                        <p
                            class="footer-contact__text"
                            v-html="contact.text"></p>
                        <z-link
                            size="s"
                            theme="light"
                            :href="`mailto:${contact.email}`"
                            class="footer-contact__email"
                            aria-label="email"
                        >
                            <span v-html="contact.email"></span>
                        </z-link>
                        <z-link
                            size="s"
                            theme="light"
                            :underlined="false"
                            class="footer-contact__tel"
                            aria-label="Phone"
                            :href="`tel:${contact.tel}`"
                        >
                            <span v-html="contact.tel"></span>
                        </z-link>
                    </div>
                    <div class="footer-contact__item show-me--tablet hide-me--default">
                        <z-link
                            size="s"
                            :underlined="false"
                            theme="light"
                            :href="texts.policyLink"
                            target="_blank"
                            :aria-label="texts.policy"
                        >
                            <span v-html="texts.policy"></span>
                        </z-link>
                    </div>
                    <div class="footer-contact__item show-me--tablet hide-me--default">
                        <z-link
                            size="s"
                            :underlined="false"
                            theme="light"
                            :href="'https://lkk.irkutskoil.ru/contract-terms/index'"
                            target="_blank"
                        >
                            <span v-html="texts.contracts"></span>
                        </z-link>
                    </div>
                    <div class="footer-contact__item show-me--tablet hide-me--default" v-if="lang === 'ru'">
                        <z-link
                            size="s"
                            :underlined="false"
                            theme="light"
                            href="/shareholders/disclosure/"
                            :aria-label="texts.info"
                        >
                            <span v-html="texts.info"></span>
                        </z-link>
                    </div>
                </div>
            </div>
            <div class="footer__bottom">
                <z-copyright
                    is-current-year
                    :text-after="texts.companyName"></z-copyright>
                <div class="footer__bottom-links">
                    <z-link
                        size="s"
                        :underlined="false"
                        theme="light"
                        :href="texts.politicaLink"
                        target="_blank"
                        :aria-label="texts.policy"
                    >
                        <span v-html="texts.policy"></span>
                    </z-link>
                    <z-link
                        v-if="lang === 'ru'"
                        size="s"
                        :underlined="false"
                        theme="light"
                        :href="'https://lkk.irkutskoil.ru/contract-terms/index'"
                        :aria-label="texts.contracts"
                        target="_blank"
                    >
                        <span v-html="texts.contracts"></span>
                    </z-link>
                    <z-link
                        v-if="lang === 'ru'"
                        size="s"
                        :underlined="false"
                        theme="light"
                        href="/shareholders/disclosure/"
                        :aria-label="texts.info"
                    >
                        <span v-html="texts.info"></span>
                    </z-link>
                </div>
                <div class="footer__socials">
                    <z-link
                        href="https://vk.com/irkutskoilcompany"
                        target="_blank"
                        aria-label="VK"
                    >
                        <z-icon
                            width="32"
                            height="32"
                            class="footer__social footer__social--vk"
                            name="social/vk" />
                    </z-link>
                    <z-link
                        href="https://max.ru/irkutskoilcompany"
                        target="_blank"
                        aria-label="MAX"
                    >
                        <z-icon
                            width="32"
                            height="32"
                            class="footer__social footer__social--max"
                            name="social/max" />
                    </z-link>
                    <z-link
                        href="https://t.me/irkutskoilcompany"
                        target="_blank"
                        aria-label="telegram"
                    >
                        <z-icon
                            width="32"
                            height="32"
                            class="footer__social footer__social--tg"
                            name="social/telegram" />
                    </z-link>
                    <z-link
                        href="https://www.youtube.com/channel/UCTFeqMD5zZLKjjp7C52YOkQ/featured"
                        target="_blank"
                        aria-label="youtube"
                    >
                        <z-icon
                            width="32"
                            height="32"
                            class="footer__social footer__social--yt"
                            name="social/youtube" />
                    </z-link>
                    <z-link
                        href=" https://rutube.ru/channel/24315454/"
                        target="_blank"
                        aria-label="rutube"
                    >
                        <z-icon
                            width="32"
                            height="32"
                            class="footer__social footer__social--rt"
                            name="social/rutube" />
                    </z-link>
                    <z-link
                        href="https://dzen.ru/irkutskoilcompany"
                        target="_blank"
                        aria-label="yandex dzen"
                    >
                        <z-icon
                            width="32"
                            height="32"
                            class="footer__social footer__social--dzen"
                            name="social/yandex_zen" />
                    </z-link>
                </div>
            </div>
        </div>
    </footer>
</template>

<script setup>
import { computed } from 'vue'
import { localize } from '@/utils/i18n'

import {useGetter} from '@/composable/useGetter'
import ZLink from '../../components/ZLink';

const { lang } = useGetter(['lang'])

const isIndex = computed(() => {
    return ['/ru/', '/en/', '/cn/', '/'].includes(window.location.pathname)
})
const texts = {
    policy: localize({
        ru: 'Политика обработки персональных данных',
        en: 'Privacy policy',
        cn: '个人数据处理政策'
    }),
    policyLink: localize({
        ru: '/personal-data-policy/',
        en: '/files/policy.pdf',
        cn: '/files/policy.pdf'
    }),
    contracts: localize({
        ru: 'Договорные условия',
        en: 'Сontractual terms'
    }),
    info: localize({
        ru: 'Раскрытие информации',
        en: 'Disclosure',
        cn: 'Disclosure'
    }),
    companyName: localize({
        ru: 'ООО &laquo;ИНК&raquo;',
        en: 'INK LLC',
        cn: 'LLC "INK"'
    }),
    politicaLink: localize({
        ru: '/personal-data-policy/',
        en: '/files/Politica.pdf',
        cn: '/files/policy.pdf'
    })
}
const logo = {
    linkFile: localize({
        ru:'/media/img/logo/logo-full-white.svg',
        en:'/media/img/logo/en-logo-full-white.svg',
        cn:'/media/img/logo/cn-logo-full-white.svg'
    })
}
const contacts = [
    {
        title: localize({
            ru: 'Иркутск',
            en: 'Irkutsk',
            cn: '伊尔库茨克市'
        }),
        text: localize({
            ru: 'пр-кт Большой Литейный, д.&nbsp;4, <br /> Россия, 664007',
            en: '4 Bolshoy Liteiny Prospekt 664007, Irkutsk, Russia ',
            cn: '俄罗斯, 664007, 伊尔库茨克市, 大利杰伊内大街4号'
        }),

        email: localize({
            ru: 'info@irkutskoil.ru',
            en: 'info@irkutskoil.ru',
            cn: 'info@irkutskoil.ru'
        }),
        tel: '+7 (3952) 211-352',
        telLink: ' +73952211352'
    },
    {
        title: localize({
            ru: 'Москва',
            en: 'Moscow',
            cn: '莫斯科市'
        }),
        text: localize({
            ru: 'Ленинградский пр-кт, д.&nbsp;36, c.&nbsp;31, офис 5, <br /> Россия, 125167',
            en: 'Office 5, Bldg. 31, 36 Leningradsky Prospekt 125167, Moscow, Russia',
            cn: '俄罗斯, 125167, 莫斯科市, 列宁格勒大街36号, 31号楼, 5号办公室'
        }),
        email: localize({
            ru: 'info@irkutskoil.ru',
            en: 'info@irkutskoil.ru',
            cn: 'info@irkutskoil.ru'
        }),
        tel: ' +7 (3952) 211-352',
        telLink: ' +73952211352'
    },
    {
        title: localize({
            ru: 'Усть-Кут',
            en: 'Ust-Kut',
            cn: '乌斯季库特市'
        }),
        text: localize({
            ru: 'ул. Калинина, д.&nbsp;6, <br /> Россия, 666780',
            en: '6 Kalinina St. 666784, Ust-Kut, Russia',
            cn: '俄罗斯, 666784, 乌斯季库特市, 加里宁街, 6号'
        }),
        email: 'info@irkutskoil.ru',
        tel: '+7 (39565) 60-233',
        linkTel: '+73956560233'
    },
    {
        title: localize({
            ru: 'Контакты',
            en: 'Contacts',
            cn: '联系方式'
        }),
        text: localize({
            ru: 'Горячая линия по&nbsp;вопросам злоупотреблений',
            en: 'Abuse Hotline',
            cn: '滥用问题的热线'
        }),

        email: localize({
            ru: 'doverie@irkutskoil.ru',
            en: 'doverie@irkutskoil.ru',
            cn: 'doverie@irkutskoil.ru'
        }),
        tel: '+7 (3952) 28-31-01',
        linkTel: '+73952283101'
    }
]
</script>

<style lang="scss">
.footer {
    background-color: var(--color-on-surface);
    @include padding-level(top, 2XL);
    @include padding-level(bottom, M);

    @include breakpoint(laptop) {
        @include padding-level(top, XL);
        @include padding-level(bottom, XL);
    }
    @include breakpoint(tablet) {
        @include padding-level(top, L);
    }

    @include breakpoint(v-tablet) {
        @include padding-level(top, M);
        @include padding-level(bottom, M);
    }
    @include breakpoint(mobile) {
        @include padding-level(top, M);
        @include padding-level(bottom, M);
    }

    &__logo {
        width: 15vw;
        max-width: 214px;
        min-width: 145px;
        @include margin-level(right, 4XL);

        @include breakpoint(laptop) {
            min-width: 143px;
            margin-right: 76px;
            width: 12vw;
        }
        @include breakpoint(tablet) {
            min-width: 136px;
            @include margin-level(right, 3XL);
        }
        @include breakpoint(v-tablet) {
            min-width: 122px;
            margin-right: 50px;
        }

        @include breakpoint(mobile) {
            min-width: 104px;
            margin-right: 0;
            @include margin-level(bottom, 2XS);
        }
    }

    &__wrapper {
        display: flex;
        width: calc(100% - 138px); // omit scroll-top-btn width + margin

        @include breakpoint(laptop) {
            width: calc(100% - 100px);
        }
        @include breakpoint(tablet) {
            width: calc(100% - 60px);
        }
        @include breakpoint(v-tablet) {
            width: calc(100% - 91px);
        }
        @include breakpoint(mobile) {
            flex-direction: column;
            width: 100%;
        }
    }

    &__bottom {
        display: flex;
        justify-content: space-between;
        align-items: center;
        @include margin-level(top, 4XL);

        @include breakpoint(laptop) {
            @include margin-level(top, 2XL);
        }

        @include breakpoint(tablet) {
            @include margin-level(top, M);
            width: calc(100% - 82px);
        }

        @include breakpoint(v-tablet) {
            flex-direction: column-reverse;
            align-items: flex-start;
            width: 100%;
        }

        @include breakpoint(mobile) {
            @include margin-level(top, 2XS);
            align-items: center;
            width: 100%;
        }
    }

    &__contacts {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        @include gap-level(3XL);
        @include margin-level(top, XS);

        @include breakpoint(laptop) {
            margin-top: 0;
            @include gap-level(2XL);
        }

        @include breakpoint(tablet) {
            grid-template-columns: repeat(2, 1fr);
            grid-gap: 40px 24px;
        }

        @include breakpoint(v-tablet) {
            grid-template-columns: repeat(1, 1fr);
            // grid-gap: 16px;
            grid-gap: 10px;
        }
        @include breakpoint(mobile) {
            text-align: center;
        }
    }

    .z-copyright {
        @include breakpoint(mobile) {
            max-width: 100%;
        }
    }

    &__bottom-links {
        display: flex;
        align-items: center;
        @include gap-level(4XL);
        @include margin-level(right, 2XL);

        @include breakpoint(tablet) {
            display: none;
        }
    }

    &__socials {
        display: flex;
        align-items: center;
        @include gap-level(2XL);

        @include breakpoint(laptop) {
            @include gap-level(L);
        }

        @include breakpoint(v-tablet) {
            margin-left: 170px;
            // margin-left: 205px;
            @include margin-level(bottom, S);
            gap: 50px;
        }

        @include breakpoint(mobile) {
            @include gap-level(L);
            margin-left: 0;
        }
    }

    &__social {
        path {
            transition: fill 0.25s ease-out, stroke 0.25s ease-out;
        }
        &--vk,
        &--rt,
        &--max,
        &--tg {
            path {
                fill: var(--color-white);
                stroke: var(--color-white);
            }

            &:hover {
                path {
                    fill: var(--color-link-light-hover);
                    stroke: var(--color-link-light-hover);
                }
            }
            &:active {
                path {
                    fill: var(--color-link-light-active);
                    stroke: var(--color-link-light-active);
                }
            }
        }
        &--yt {
            path {
                fill: var(--color-white);
            }
            &:hover {
                path {
                    fill: var(--color-link-light-hover);
                }
            }
            &:active {
                path {
                    fill: var(--color-link-light-active);
                }
            }
        }
        &--dzen {
            path {
                &:first-child {
                    fill: var(--color-white);
                }
                &:nth-child(2) {
                    fill: transparent;
                }
            }

            &:hover {
                path {
                    &:first-child {
                        fill: var(--color-link-light-hover);
                    }
                    &:nth-child(2) {
                        fill: transparent;
                    }
                }
            }
            &:active {
                path {
                    &:first-child {
                        fill: var(--color-link-light-active);
                    }
                    &:nth-child(2) {
                        fill: transparent;
                    }
                }
            }
        }
    }
}

.footer-contact {
    color: var(--color-white);
    @include typo-level(S);

    &__item {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        max-width: 227px;

        @include breakpoint(tablet) {
            max-width: 100%;
        }

        @include breakpoint(mobile) {
            .z-link {
                font-size: 14px !important;
            }
        }

        & > * {
            @include margin-level(bottom, 2XS);

            @include breakpoint(tablet) {
                margin-bottom: 8px;
            }

            &:last-child {
                margin-bottom: 0;
            }
        }
    }
    &__title {
        font-weight: 700;
    }
    &__text {
        @include breakpoint(tablet) {
            max-width: 298px;
            br {
                display: none;
            }
        }
        &--highlighted {
            @include typo-level(M);
            font-weight: 500;
            line-height: 1.3;
        }
    }
    &__tel {
        @include typo-level(L);
        white-space: nowrap;
    }
}
</style>
